import { CheckCircle2, ListChecks } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface AcceptanceCriterion {
  id?: string;
  description?: string;
  check?: string;
}

interface AcceptanceCriteriaCardProps {
  criteria?: AcceptanceCriterion[] | null;
  className?: string;
}

/**
 * Read-only checklist of a job's machine-checkable acceptance criteria. These
 * are the objective conditions the agent's work must satisfy before escrow
 * releases — the buyer's guarantee made concrete.
 */
export function AcceptanceCriteriaCard({ criteria, className }: AcceptanceCriteriaCardProps) {
  const items = Array.isArray(criteria) ? criteria.filter((c) => c && (c.description || c.check)) : [];

  return (
    <Card className={`glass-panel ${className ?? ""}`}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-muted-foreground">
          <ListChecks className="h-4 w-4" /> Acceptance Criteria
        </CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground leading-relaxed">
            No machine-checkable acceptance criteria were set for this job. Approval is at the poster's discretion.
          </p>
        ) : (
          <ul className="space-y-3">
            {items.map((c, i) => (
              <li key={c.id || i} className="flex gap-3">
                <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
                <div className="space-y-1 min-w-0">
                  <p className="text-sm leading-snug">{c.description || c.check}</p>
                  {c.check && c.description && (
                    <code className="block text-[11px] font-mono text-primary/70 break-all bg-muted rounded px-2 py-1">
                      {c.check}
                    </code>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
